import alt from "../alt";
import values from "lodash/values";
import indexBy from "lodash/keyBy";
import TicketFetcher from "../utils/TicketFetcher";
import TicketUtil from "../utils/TicketUtil";
import TicketActions from "../actions/TicketActions";
import PersonStore from "../stores/PersonStore";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

class TicketStore {
  constructor() {
    this.bindActions(TicketActions);

    this.state = {
      hasUpdatedAll: false,
      tickets: {},
      transferComplete: false
    };

    this.exportPublicMethods({
      getTickets: this.getTickets.bind(this),
      getTicketById: this.getTicketById.bind(this),
      getTicketsByEventId: this.getTicketsByEventId.bind(this)
    });
  }

  getTickets() {
    return Object.values(this.state.tickets);
  }

  getTicketById(id) {
    return this.state.tickets[id];
  }

  getTicketsByEventId(id) {
    return values(this.state.tickets).filter(t => t.event.id === id);
  }

  onFetchAll() {
    TicketFetcher.fetchAll(PersonStore.getState().token)
      .then(data => {
        TicketActions.updateTickets(indexBy(data, "id"));
      })
      .catch(TicketActions.fetchFailed);
  }

  onFetchOne(id) {
    TicketFetcher.fetchOne(PersonStore.getState().token, id)
      .then(data => {
        // Persist delete error
        if (this.state.tickets[id] && this.state.tickets[id].deleteError) {
          data.deleteError = this.state.tickets[id].deleteError;
        }

        TicketActions.updateTicket(id, data);
      })
      .catch(err => {
        if (!this.state.tickets[id]) {
          return; // Swallow error - suggests we just deleted the ticket
        } else {
          TicketActions.fetchFailed(err);
        }
      });
  }

  onUpdateTickets(tickets) {
    this.setState({ tickets, hasUpdatedAll: true });
  }

  onUpdateTicket(data) {
    let tickets = Object.assign({}, this.state.tickets);

    if (data.ticket) {
      tickets[data.id] = data.ticket;
    } else {
      delete tickets[data.id];
    }

    this.setState({ tickets: tickets });
  }

  onDeleteTicket(id) {
    let ticket = this.state.tickets[id];
    TicketUtil.delete(PersonStore.getState().token, id)
      .then(data => {
        TicketActions.updateTicket(id, undefined);
        GlobalNotificationActions.notify.defer(
          `Ticket #${id} has been canceled successfully.`
        );
        if (window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION) {
          window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION({
            target: ticket && ticket.event ? { event_id: ticket.event.id } : {},
            action: "ticket_canceled"
          });
        }
      })
      .catch(err => {
        let ticket = this.state.tickets[id];
        if (ticket) {
          ticket.deleteError = err.message;
          GlobalNotificationActions.notify.defer(
            `Unable to cancel ticket: ${err.message}`
          );
          TicketActions.updateTicket(id, ticket);
        }
      });
  }

  onDownloadPdf(event) {
    TicketUtil.downloadPdf(PersonStore.getState().token, event);
  }

  onTransferTickets({ ids, destinationEmail }) {
    this.setState({ isTransferring: true });
    TicketUtil.transfer(PersonStore.getState().token, { ids, destinationEmail })
      .then(data => {
        this.setState({
          tickets: Object.assign(
            {},
            this.state.tickets,
            indexBy(data.tickets, "id")
          )
        });
        GlobalNotificationActions.notify.defer(
          `Ticket transfer has been sent successfully.`
        );
        if (window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION) {
          window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION({
            target: {},
            action: "ticket_transfer_initiated"
          });
        }
      })
      .catch(err => {
        window.alert(`Unable to transfer tickets: ${err.message}`);
      })
      .finally(() => {
        this.setState({ isTransferring: false });
      });
  }

  onCancelTicketTransfer(id) {
    this.setState({ isTransferring: true });
    TicketUtil.cancelTransfer(PersonStore.getState().token, id)
      .then(data => {
        this.setState({
          tickets: Object.assign(
            {},
            this.state.tickets,
            indexBy(data.tickets, "id")
          )
        });
        GlobalNotificationActions.notify.defer(
          `Tickets transfer canceled successfully.`
        );
        if (window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION) {
          window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION({
            target: {},
            action: "ticket_transfer_canceled"
          });
        }
      })
      .catch(err => {
        window.alert(`Unable to cancel transfer: ${err.message}`);
      })
      .finally(() => {
        this.setState({ isTransferring: false });
      });
  }

  onResendTicketTransfer(id) {
    this.setState({ isTransferring: true });
    TicketUtil.resendTransfer(PersonStore.getState().token, id)
      .then(data => {
        GlobalNotificationActions.notify.defer(
          `Tickets transfer resent successfully.`
        );
      })
      .catch(err => {
        window.alert(`Unable to resend transfer: ${err.message}`);
      })
      .finally(() => {
        this.setState({ isTransferring: false });
      });
  }

  onAcceptTicketTransfer({ id, token }) {
    TicketUtil.acceptTransfer(PersonStore.getState().token, id, token)
      .then(data => {
        this.setState({
          tickets: Object.assign(
            {},
            this.state.tickets,
            indexBy(data.tickets, "id")
          ),
          transferComplete: true
        });
        GlobalNotificationActions.notify.defer(
          `Tickets transfer accepted successfully.`
        );

        // Sundance: Redirect back to Sundance account page
        if (TENANT.sundance_login) {
          window.location.href = `${
            TENANT.sundance_external_schedule_url ||
            "https://fixme-sundance-redirect"
          }`;
        }
      })
      .catch(err => {
        window.alert(`Unable to accept transfer: ${err.message}`);
      });
  }

  onResetAcceptTicketTransfer() {
    this.setState({ transferComplete: false });
  }

  onFetchFailed(e) {
    throw e;
  }
}

export default alt.createStore(TicketStore, "TicketStore");



// WEBPACK FOOTER //
// ./src/app/stores/TicketStore.js